import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { CurrencyGbp } from "@phosphor-icons/react";
import {
  PUBLIC_BUDGET_PRESETS,
  parsePublicBudgetInput,
  sanitisePublicBudget,
} from "../lib/publicCompetitionContract";

const isPreset = (value) => PUBLIC_BUDGET_PRESETS.includes(Number(value));

const BudgetAmountControl = forwardRef(function BudgetAmountControl({
  budget,
  onBudgetChange,
  testIdPrefix = "",
  showApply = false,
  applyLabel = "Apply",
  tone = "dark",
}, ref) {
  const canonicalBudget = sanitisePublicBudget(budget, 20);
  const [inputValue, setInputValue] = useState(String(canonicalBudget));
  const [selection, setSelection] = useState(isPreset(canonicalBudget) ? canonicalBudget : "other");
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const inputId = `${testIdPrefix || "canonical-"}budget-amount`;
  const errorId = `${inputId}-error`;

  useEffect(() => {
    const next = sanitisePublicBudget(budget, 20);
    setInputValue(String(next));
    setSelection(isPreset(next) ? next : "other");
    setError("");
  }, [budget]);

  const commit = () => {
    const parsed = parsePublicBudgetInput(inputValue);
    if (!parsed.ok) {
      setError(parsed.error);
      return null;
    }
    setInputValue(String(parsed.value));
    setSelection(isPreset(parsed.value) ? parsed.value : "other");
    setError("");
    onBudgetChange?.(parsed.value);
    return parsed.value;
  };

  const choosePreset = (value) => {
    setInputValue(String(value));
    setSelection(value);
    setError("");
    onBudgetChange?.(value);
  };

  const chooseOther = () => {
    setSelection("other");
    setError("");
    if (isPreset(selection)) setInputValue("");
    inputRef.current?.focus();
  };

  useImperativeHandle(ref, () => ({
    commit,
    focusCustom: chooseOther,
  }));

  const isLight = tone === "light";
  const inputShell = isLight
    ? "bg-slate-50 border-slate-200 focus-within:border-[var(--cc-bronze)]"
    : "bg-white/5 border-white/15 focus-within:border-[var(--cc-gold)]";
  const inputText = isLight ? "text-[var(--cc-text-dark)]" : "text-white";
  const inactivePreset = isLight
    ? "border-slate-300 text-slate-600 hover:border-slate-500"
    : "border-white/20 text-white/70 hover:border-white/40";
  const activePreset = isLight
    ? "bg-[var(--cc-navy)] border-[var(--cc-navy)] text-white"
    : "bg-[var(--cc-gold)] border-[var(--cc-gold)] text-[var(--cc-navy)]";

  return (
    <div className="w-full min-w-0" data-testid={`${testIdPrefix}budget-control`}>
      <label htmlFor={inputId} className={`overline block mb-2 ${isLight ? "text-[var(--cc-text-dark-muted)]" : "text-white/50"}`}>
        Your budget
      </label>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
        <div className="flex-1 min-w-0">
          <div className={`flex items-center border rounded px-3 ${inputShell}`}>
            <CurrencyGbp size={18} className={isLight ? "text-slate-400" : "text-white/40"} />
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              inputMode="decimal"
              value={inputValue}
              placeholder="Enter your budget here"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
              onChange={(event) => {
                setInputValue(event.target.value);
                setSelection("other");
                setError("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  commit();
                }
              }}
              data-testid={`${testIdPrefix}budget-input`}
              className={`flex-1 min-w-0 bg-transparent text-lg font-mono-num py-2.5 px-2 outline-none ${inputText}`}
            />
          </div>
          {error && (
            <p id={errorId} role="alert" className={`mt-2 text-xs ${isLight ? "text-red-700" : "text-red-300"}`}>
              {error}
            </p>
          )}
        </div>
        {showApply && (
          <button
            type="button"
            onClick={commit}
            data-testid={`${testIdPrefix}budget-apply`}
            className="btn-gold px-5 py-2.5 rounded text-sm sm:flex-none"
          >
            {applyLabel}
          </button>
        )}
      </div>
      <div className="flex max-w-full flex-wrap gap-2 mt-3" aria-label="Budget presets">
        {PUBLIC_BUDGET_PRESETS.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={selection === value}
            data-testid={`${testIdPrefix}quick-budget-${value}`}
            onClick={() => choosePreset(value)}
            className={`min-h-9 px-3 py-1.5 rounded-full text-xs border transition-colors ${selection === value ? activePreset : inactivePreset}`}
          >
            {"\u00a3"}{value}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={selection === "other"}
          data-testid={`${testIdPrefix}quick-budget-other`}
          onClick={chooseOther}
          className={`min-h-9 px-3 py-1.5 rounded-full text-xs border transition-colors ${selection === "other" ? activePreset : inactivePreset}`}
        >
          Other
        </button>
      </div>
    </div>
  );
});

export default BudgetAmountControl;
